import { isAxiosError } from 'axios';
import { computed, ref, type Ref } from 'vue';

import {
  downloadBlobFile,
  useCustomToast,
  useDocumentSearchTypeStore,
  type Pagination,
  type SearchFilter,
} from '@/shared/lib';

import { documentManagementExportApi } from '../api/document-management-export.api';
import { DOCUMENT_MANAGEMENT_EXPORT_GENERIC_MESSAGE } from '../config/messages';

function getExportErrorSubtitle(error: unknown): string {
  if (isAxiosError(error)) {
    return DOCUMENT_MANAGEMENT_EXPORT_GENERIC_MESSAGE;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return DOCUMENT_MANAGEMENT_EXPORT_GENERIC_MESSAGE;
}

export function useDocumentManagementExport(options: {
  searchTerm: Ref<string>;
  sort: Ref<string>;
  order: Ref<string>;
  filter: Ref<SearchFilter>;
  metadata: Ref<Pagination | null | undefined>;
}) {
  const toast = useCustomToast();
  const { searchType } = useDocumentSearchTypeStore();
  const isExporting = ref(false);

  const isExportDisabled = computed(() => {
    if (isExporting.value) {
      return true;
    }
    const total = options.metadata.value?.total;
    if (total === undefined) {
      return false;
    }
    return total === 0;
  });

  const handleExportToExcel = async () => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      });

      const { blob, exportedCount, filename } =
        await documentManagementExportApi.exportGestaoDocumentsExcel({
          searchTerm: options.searchTerm.value,
          searchType: searchType.value,
          sort: options.sort.value,
          order: options.order.value,
          filter: options.filter.value,
        });

      downloadBlobFile(blob, filename);

      const successMessage =
        exportedCount === 1 ? '1 documento exportado' : `${exportedCount} documentos exportados`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch (error) {
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: getExportErrorSubtitle(error),
        type: 'error',
      });
    } finally {
      isExporting.value = false;
    }
  };

  return {
    handleExportToExcel,
    isExporting,
    isExportDisabled,
  };
}
